import React, { useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { FiltersBar, AGING_PRESETS } from './components/FiltersBar.jsx';
import { MapCanvas } from './components/MapCanvas.jsx';
import { ONUTable } from './components/ONUTable.jsx';
import { ONUDrawer } from './components/ONUDrawer.jsx';
import { CommandPalette } from './components/CommandPalette.jsx';
import { useSnapshot } from './hooks/useSnapshot.js';
import { useCmdK } from './hooks/useGlobalHotkey.js';
import { agingMinutes } from './lib/format.js';

const qc = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

const PAGE_SIZE = 200;

/** Debounce simples sem dep externa. */
function useDebounced(value, ms = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// Efetivo timestamp do aging (mesma regra do backend: last_change_at_olt
// é o ground truth da OLT — sobrevive a restart do poller — com fallback
// pro status_since local).
function effectiveSinceISO(onu) {
  return onu.last_change_at_olt || onu.status_since || onu.observed_at || null;
}

function MapPageInner() {
  // UI state (todos os filtros + sort + page) — APENAS client-side desde v3.
  const [statusFilter, setStatusFilter] = useState('bad');
  const [searchInput, setSearchInput] = useState('');
  const [selectedOlts, setSelectedOlts] = useState([]);
  const [agingPresetKey, setAgingPresetKey] = useState('any');
  // Default: aging ASC → ONUs com menor tempo offline primeiro ("agora", 3m, 12m…).
  // Triagem do NOC prioriza incidentes recentes (cadastros antigos abandonados
  // ficam no fim). aging = now - status_since; o compare trata aging/status_since
  // identicamente, mas o sortBy='aging' alinha com o nome da coluna ("Há").
  const [sortBy, setSortBy] = useState('aging');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [selectedSn, setSelectedSn] = useState(null);
  const [paletteOnu, setPaletteOnu] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useCmdK(() => setPaletteOpen(true));

  // Debounce search (200ms — mais agressivo agora que é só filter local).
  const search = useDebounced(searchInput, 200);

  // V3: 1 request a cada 30s + cache server-side 25s.
  const {
    rows: allRows,
    facets,
    generatedAt,
    source,
    isLoading,
    isFetching,
    error,
  } = useSnapshot({ status: statusFilter });

  // Extrai {min, max} do preset de aging.
  const agingPreset = AGING_PRESETS.find((p) => p.key === agingPresetKey) || AGING_PRESETS[0];
  const minAgingMin = agingPreset.min || 0;
  const maxAgingMin = agingPreset.max || 0;

  // Reset page=0 quando filtros mudam.
  useEffect(() => {
    setPage(0);
  }, [statusFilter, search, selectedOlts, agingPresetKey, sortBy, sortOrder]);

  // ---------- Pipeline client-side: filter → sort → paginate ----------

  // 1) Filtros não-status (status já vem do snapshot).
  const filtered = useMemo(() => {
    const oltSet = new Set(selectedOlts);
    const s = (search || '').trim().toLowerCase();
    const hasOltFilter = oltSet.size > 0;
    const hasSearch = s.length > 0;
    const hasAgingFilter = minAgingMin > 0 || maxAgingMin > 0;
    const now = Date.now();

    return allRows.filter((o) => {
      if (hasOltFilter && !oltSet.has(o.olt_id)) return false;

      if (hasSearch) {
        const hay = [
          o.person_name,
          o.contract_code,
          o.contract_id,
          o.onu_sn,
          o.onu_desc,
          o.postal_code,
          o.olt_code,
          o.olt_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }

      if (hasAgingFilter) {
        const iso = effectiveSinceISO(o);
        const min = agingMinutes(iso);
        if (min == null) return false;
        // [min, max) — exclusivo no max. max=0 → sem limite superior.
        if (minAgingMin > 0 && min < minAgingMin) return false;
        if (maxAgingMin > 0 && min >= maxAgingMin) return false;
      }

      return true;
    });
  }, [allRows, selectedOlts, search, minAgingMin, maxAgingMin]);

  // 2) Sort client-side.
  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const arr = [...filtered];
    const cmp = (a, b) => {
      let va, vb;
      if (sortBy === 'aging' || sortBy === 'status_since') {
        va = new Date(effectiveSinceISO(a) || 0).getTime();
        vb = new Date(effectiveSinceISO(b) || 0).getTime();
      } else if (sortBy === 'person_name') {
        va = (a.person_name || '').toLowerCase();
        vb = (b.person_name || '').toLowerCase();
      } else if (sortBy === 'onu_desc') {
        va = (a.onu_desc || '').toLowerCase();
        vb = (b.onu_desc || '').toLowerCase();
      } else if (sortBy === 'contract_id') {
        va = Number(a.contract_id) || 0;
        vb = Number(b.contract_id) || 0;
      } else if (sortBy === 'olt_id') {
        va = a.olt_id || 0;
        vb = b.olt_id || 0;
      } else if (sortBy === 'signal_rx_dbm') {
        va = a.signal_rx_dbm ?? -999;
        vb = b.signal_rx_dbm ?? -999;
      } else {
        va = a[sortBy];
        vb = b[sortBy];
      }
      if (va < vb) return -1;
      if (va > vb) return 1;
      // Tie-breaker estável: onu_sn ASC
      return (a.onu_sn || '').localeCompare(b.onu_sn || '');
    };
    arr.sort(cmp);
    if (sortOrder === 'desc') arr.reverse();
    return arr;
  }, [filtered, sortBy, sortOrder]);

  const matched = sorted.length;
  const pageCount = Math.max(1, Math.ceil(matched / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);

  // 3) Paginação client-side: slice da página atual pra tabela.
  const pageRows = useMemo(
    () => sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [sorted, safePage]
  );

  // Mapa usa TODAS as ONUs filtradas (sorted) — independente de paginação.
  // Esse é o ponto-chave: o NOC vê todos os pins ao mesmo tempo.
  const mapRows = sorted;

  // ---------- Selection / drawer ----------

  const selected = useMemo(() => {
    if (!selectedSn) return null;
    const inRows = sorted.find((o) => o.onu_sn === selectedSn);
    if (inRows) return inRows;
    if (paletteOnu && paletteOnu.onu_sn === selectedSn) return paletteOnu;
    return null;
  }, [sorted, selectedSn, paletteOnu]);

  // ---------- Empty hint ----------

  const _hasUserFilters =
    Boolean(search) || selectedOlts.length > 0 || agingPresetKey !== 'any';
  const _statusLabelMap = {
    LOS: 'LOS puro (raw=6 da Huawei)',
    dying_gasp: 'dying gasp',
    power_off: 'power off',
    config_failure: 'config failure',
  };
  let emptyHint = null;
  if (!isLoading && !error && matched === 0) {
    if (statusFilter === 'bad' && !_hasUserFilters) {
      emptyHint = '🎉 Nenhuma ONU em problema agora.';
    } else if (_statusLabelMap[statusFilter] && !_hasUserFilters) {
      emptyHint = `Nenhuma ONU em ${_statusLabelMap[statusFilter]} no momento. Veja "Em problema" para o consolidado.`;
    } else if (allRows.length > 0 && _hasUserFilters) {
      emptyHint = 'Nenhuma ONU corresponde aos filtros.';
    } else {
      emptyHint = 'Nenhuma ONU corresponde aos filtros.';
    }
  }

  // Facets do snapshot (globais). by_olt vem como array.
  const byOlt = facets?.by_olt || [];
  const byStatus = facets?.by_status || {};
  const agingBuckets = facets?.aging_buckets || {};
  const totalAll = facets?.total ?? 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        background: 'white',
      }}
    >
      <FiltersBar
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        search={searchInput}
        onSearchChange={setSearchInput}
        selectedOlts={selectedOlts}
        onOltsChange={setSelectedOlts}
        agingPresetKey={agingPresetKey}
        onAgingPresetChange={setAgingPresetKey}
        byOlt={byOlt}
        byStatus={byStatus}
        agingBuckets={agingBuckets}
        shownCount={pageRows.length}
        matchedCount={matched}
        totalCount={totalAll}
        source={source}
        isFetching={isFetching}
        generatedAt={generatedAt}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Mapa (esquerda, 60%) */}
        <div
          style={{
            flex: '0 0 60%',
            borderRight: '1px solid #e5e7eb',
            position: 'relative',
          }}
        >
          {error && (
            <div
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 5,
                padding: '10px 16px',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: 8,
                fontSize: 13,
                maxWidth: 400,
              }}
            >
              Erro carregando API v2: {String(error.message || error)}
            </div>
          )}
          <MapCanvas
            onus={mapRows}
            onSelectOnu={setSelectedSn}
            selectedSn={selectedSn}
            statusFilter={statusFilter}
            matchedCount={matched}
            focusOn={
              selected
                ? {
                    lat: Number(selected.install_lat),
                    lng: Number(selected.install_lng),
                  }
                : null
            }
          />
        </div>

        {/* Tabela (direita, 40%) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ONUTable
            onus={pageRows}
            selectedSn={selectedSn}
            onSelectOnu={setSelectedSn}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={(by, order) => {
              setSortBy(by);
              setSortOrder(order);
            }}
            loading={isLoading}
            emptyHint={emptyHint}
            page={safePage}
            pageCount={pageCount}
            pageSize={PAGE_SIZE}
            matchedCount={matched}
            onPageChange={setPage}
          />
        </div>
      </div>

      <ONUDrawer
        onu={selected}
        onClose={() => {
          setSelectedSn(null);
          setPaletteOnu(null);
        }}
      />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onSelectOnu={(onu) => {
          setSelectedSn(onu.onu_sn);
          setPaletteOnu(onu);
        }}
      />
    </div>
  );
}

export default function MapPage() {
  return (
    <QueryClientProvider client={qc}>
      <MapPageInner />
    </QueryClientProvider>
  );
}
